"use client";

import { useState, useEffect } from "react";
import styles from "./ContactSplitSection.module.css";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { Select } from "@/components/ui/Select";
import Button from "@/components/ui/Button";
import { getMetaClickIds, persistMetaLead, trackPixelEvent } from "@/lib/meta/browser";
import Script from "next/script";
import { getRecaptchaToken } from "@/lib/recaptcha-browser";

interface ContactProps {
    hideSubject?: boolean;
    categoryId?: number;
    origin?: string;
    label?: string;
}

export default function ContactSplitSection({ hideSubject = false, categoryId, origin, label }: ContactProps) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [contracts, setContracts] = useState("");
    const [message, setMessage] = useState("");
    const [honeypot, setHoneypot] = useState(""); // Anti-bot field
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");

    // UTM state
    const [utmSource, setUtmSource] = useState("");
    const [utmMedium, setUtmMedium] = useState("");
    const [utmCampaign, setUtmCampaign] = useState("");
    const [utmContent, setUtmContent] = useState("");
    const [utmTerm, setUtmTerm] = useState("");

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        setUtmSource(queryParams.get("utm_source") || "");
        setUtmMedium(queryParams.get("utm_medium") || "");
        setUtmCampaign(queryParams.get("utm_campaign") || "");
        setUtmContent(queryParams.get("utm_content") || "");
        setUtmTerm(queryParams.get("utm_term") || "");
    }, []);

    const formatPhone = (value: string) => {
        const numbers = value.replace(/\D/g, "");
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    };

    const isValidEmail = (value: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    };

    const subjectOptions = [
        { value: "comercial", label: "Comercial" },
        { value: "financeiro", label: "Financeiro" },
        { value: "suporte", label: "Suporte" },
        { value: "parceria", label: "Parceria" },
    ];

    const contractsOptions = [
        { value: "0-50", label: "0 - 50" },
        { value: "51-200", label: "51 - 200" },
        { value: "201-500", label: "201 - 500" },
        { value: "501-1000", label: "501 - 1000" },
        { value: "1000+", label: "Mais de 1000" },
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

        // Validation
        if (phone.replace(/\D/g, "").length < 10) {
            setStatus("error");
            setErrorMessage("Por favor, insira um telefone válido com DDD.");
            return;
        }

        if (!isValidEmail(email)) {
            setStatus("error");
            setErrorMessage("Por favor, insira um e-mail válido.");
            return;
        }

        try {
            // Get reCaptcha token
            const recaptchaToken = await getRecaptchaToken();

            const meta = getMetaClickIds();
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    nome: name,
                    telefone: phone,
                    email: email,
                    assunto: hideSubject ? "Contato via LP" : subject,
                    contratos: contracts,
                    mensagem: message,
                    categoria: categoryId,
                    origem: origin,
                    etiqueta: label,
                    utmSource,
                    utmMedium,
                    utmCampaign,
                    utmContent,
                    utmTerm,
                    _honeypot: honeypot, // Pass the trap to the server
                    recaptchaToken, // Pass token to verify
                    eventId: meta.eventId,
                    fbp: meta.fbp,
                    fbc: meta.fbc,
                    eventSourceUrl: meta.eventSourceUrl,
                }),
            });

            const data = await response.json().catch(() => {
                throw new Error("Não foi possível confirmar o envio da mensagem. Tente novamente.");
            });

            if (!response.ok || data?.success !== true || data?.error) {
                throw new Error("Não foi possível confirmar o envio da mensagem. Tente novamente.");
            }

            setStatus("success");
            persistMetaLead({ email, phone, name });
            trackPixelEvent("Lead", data.eventId || meta.eventId, {
                content_name: hideSubject ? "Contato via LP" : subject,
                content_category: "lead",
                status: "submitted",
            });
            // Reset form
            setName("");
            setPhone("");
            setEmail("");
            setSubject("");
            setContracts("");
            setMessage("");
        } catch (error) {
            console.error("Error submitting contact form:", error);
            setStatus("error");
            setErrorMessage(error instanceof Error ? error.message : "Erro crítico ao conectar com o servidor.");
        }
    };

    return (
        <section className={styles.section}>
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
                <Script
                    id="recaptcha-enterprise"
                    src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
                    strategy="afterInteractive"
                />
            )}
            <div className={styles.container}>
                {/* Left Content */}
                <div className={styles.contextSide}>
                    <span className={styles.eyebrow}>Contato comercial</span>
                    <h1 className={styles.title}>
                        Pronto para organizar e evoluir sua <span>operação imobiliária?</span>
                    </h1>
                    <p className={styles.description}>
                        Converse com nosso time e entenda como o ecossistema Flexpro pode ajudar sua imobiliária a estruturar processos, automatizar rotinas e centralizar informações.
                    </p>

                    <div className={styles.benefitsList}>
                        <div className={styles.benefitItem}>
                            <div className={styles.checkIcon}></div>
                            <p className={styles.benefitText}>Receba orientações de acordo com a realidade da sua operação.</p>
                        </div>
                        <div className={styles.benefitItem}>
                            <div className={styles.checkIcon}></div>
                            <p className={styles.benefitText}>Entenda quais soluções fazem mais sentido para o seu momento.</p>
                        </div>
                        <div className={styles.benefitItem}>
                            <div className={styles.checkIcon}></div>
                            <p className={styles.benefitText}>Solicite uma demonstração das plataformas Flexpro.</p>
                        </div>
                        <div className={styles.benefitItem}>
                            <div className={styles.checkIcon}></div>
                            <p className={styles.benefitText}>Tire dúvidas sobre planos, implantação, módulos e próximos passos.</p>
                        </div>
                    </div>

                    <div className={styles.supportCard}>
                        <div className={styles.supportIcon} aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path d="M12 4.5c-4.14 0-7.5 2.91-7.5 6.5 0 1.78.82 3.39 2.15 4.56l-.65 3.19 3.27-1.57c.84.21 1.76.32 2.73.32 4.14 0 7.5-2.91 7.5-6.5S16.14 4.5 12 4.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8.5 11h.01M12 11h.01M15.5 11h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                        </div>
                        <div>
                            <strong>Atendimento pensado para imobiliárias</strong>
                            <span>Nosso time entende a rotina de locação, vendas, financeiro, condomínio e gestão operacional.</span>
                        </div>
                    </div>
                </div>

                {/* Right Form */}
                <div className={styles.formSide}>
                    {status === "success" ? (
                        <div className={styles.successMessage}>
                            <h3>Mensagem enviada!</h3>
                            <p>Obrigado pelo contato. Nosso time retornará em breve.</p>
                            <Button onClick={() => setStatus("idle")}>Enviar nova mensagem</Button>
                        </div>
                    ) : (
                        <form className={styles.inputGroup} onSubmit={handleSubmit}>
                            {/* Honeypot Field - Hidden from humans, visible to bots */}
                            <input
                                type="text"
                                name="_honeypot"
                                style={{ display: 'none', visibility: 'hidden', position: 'absolute', left: '-9999px', top: '-9999px' }}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                            />

                            {/* Name Field */}
                            <Input
                                id="name"
                                type="text"
                                label="Nome completo"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />

                            {/* Phone and Email Row */}
                            <div className={styles.row}>
                                <Input
                                    id="phone"
                                    type="tel"
                                    label="Telefone"
                                    value={phone}
                                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                                    placeholder="(00) 00000-0000"
                                    required
                                />
                                <Input
                                    id="email"
                                    type="email"
                                    label="E-mail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Custom Selects */}
                            {!hideSubject && (
                                <Select
                                    id="subject"
                                    label="Assunto"
                                    options={subjectOptions}
                                    value={subject}
                                    onChange={setSubject}
                                />
                            )}

                            <Select
                                id="contracts"
                                label="Quantidade de contratos"
                                options={contractsOptions}
                                value={contracts}
                                onChange={setContracts}
                            />

                            {/* Message Field */}
                            <Textarea
                                id="message"
                                label="Mensagem"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                            />

                            {status === "error" && (
                                <p className={styles.errorMessage}>{errorMessage}</p>
                            )}

                            <Button
                                type="submit"
                                className={styles.submitButton}
                                disabled={status === "loading"}
                            >
                                {status === "loading" ? "Enviando..." : "Enviar"}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
